import type { AdminSessionRow } from "../../lib/admin/types";
import { toDate } from "../../lib/admin/types";
import { formatMode } from "../../lib/admin/formatters";
import { useT } from "../../lib/use-t";
import { StatCard } from "./StatCard";

export type StatRowProps = {
  /** null = loading skeleton. */
  sessions: AdminSessionRow[] | null;
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * Consecutive-calendar-day streak over local-tz session days. The streak
 * only counts if the most recent session day is today or yesterday — a
 * gap of two days breaks it. Honest client derivation (no streak field
 * exists in the API), allowed by _DECISIONS.md.
 */
function computeStreak(sessions: AdminSessionRow[]): number {
  const days = new Set<string>();
  for (const s of sessions) {
    const d = toDate(s.startedAt);
    if (d) days.add(dayKey(d));
  }
  if (days.size === 0) return 0;

  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  // Anchor: streak survives if the last session was today OR yesterday.
  if (!days.has(dayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(dayKey(cursor))) return 0;
  }
  let streak = 0;
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function StatRow({ sessions }: StatRowProps) {
  const t = useT();

  if (sessions === null) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={
              "h-24 animate-pulse rounded-[var(--radius-lg)] bg-[var(--panel-soft)]" +
              (i >= 2 ? " hidden lg:block" : "")
            }
            aria-hidden
          />
        ))}
      </div>
    );
  }

  const finished = sessions.filter((s) => s.percentage != null);

  const avg =
    finished.length > 0
      ? Math.round(
          (finished.reduce((sum, s) => sum + (s.percentage ?? 0), 0) / finished.length) * 100,
        )
      : null;

  const best = finished.reduce<AdminSessionRow | null>((acc, s) => {
    if (acc === null) return s;
    return (s.percentage ?? 0) > (acc.percentage ?? 0) ? s : acc;
  }, null);
  const bestPct = best?.percentage != null ? Math.round(best.percentage * 100) : null;
  const bestDate = best ? toDate(best.startedAt) : null;

  const streak = computeStreak(sessions);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <StatCard
        label={t("history.voortgang.statAvg")}
        value={
          avg !== null ? (
            <>
              {avg}
              <small className="text-base font-medium text-[var(--ink-faint)]">
                {t("history.voortgang.statAvgOutOf")}
              </small>
            </>
          ) : (
            "—"
          )
        }
      />
      <StatCard
        label={t("history.voortgang.statStreak")}
        value={
          <>
            {streak}
            <small className="text-base font-medium text-[var(--ink-faint)]">
              {" "}
              {streak === 1
                ? t("history.voortgang.statStreakDay")
                : t("history.voortgang.statStreakDays")}
            </small>
          </>
        }
        sub={
          streak > 0
            ? t("history.voortgang.statStreakKeepGoing")
            : t("history.voortgang.statStreakNone")
        }
      />
      <StatCard
        className="hidden lg:block"
        label={t("history.voortgang.statBest")}
        value={bestPct !== null ? bestPct : "—"}
        sub={
          best && bestDate
            ? `${bestDate.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} · ${formatMode(best.mode)}`
            : undefined
        }
      />
      <StatCard
        className="hidden lg:block"
        label={t("history.voortgang.statSessions")}
        value={sessions.length}
        sub={t("history.voortgang.statSessionsTotal")}
      />
    </div>
  );
}
